/**
 * Read the first value for `key` from any FormData implementation.
 * React Native's FormData polyfill only implements append() and getAll() — no get() or set().
 * See node_modules/react-native/Libraries/Network/FormData.js.
 */
export function getFormDataValue(formData: FormData, key: string): FormDataEntryValue | null {
	const fd = formData as FormData & { get?: (k: string) => FormDataEntryValue | null };
	if (typeof fd.get === 'function') {
		return fd.get(key);
	}
	return formData.getAll(key)[0] ?? null;
}

/**
 * Type-safe wrapper around FormData that provides better type inference
 * and validation for form data operations.
 */
export class TypedFormData<T extends Record<string, unknown>> {
	private formData: FormData;

	constructor(formData?: FormData) {
		this.formData = formData || new FormData();
	}

	/**
	 * Get a typed value from the FormData
	 */
	getTypedValue<K extends keyof T>(key: K): T[K] | null {
		return getFormDataValue(this.formData, key as string) as T[K] | null;
	}

	/**
	 * Set a typed value in the FormData. Falls back to append() on FormData
	 * implementations without set() (React Native polyfill).
	 */
	setTypedValue<K extends keyof T>(key: K, value: T[K]): void {
		if (value !== null && value !== undefined) {
			const fd = this.formData as FormData & { set?: (k: string, v: string | Blob) => void };
			if (typeof fd.set === 'function') {
				fd.set(key as string, value as string | Blob);
			} else {
				fd.append(key as string, value as string | Blob);
			}
		}
	}

	/**
	 * Append a typed value to the FormData
	 */
	appendTypedValue<K extends keyof T>(key: K, value: T[K]): void {
		if (value !== null && value !== undefined) {
			this.formData.append(key as string, value as string | Blob);
		}
	}

	/**
	 * Get the underlying FormData instance
	 */
	getFormData(): FormData {
		return this.formData;
	}

	/**
	 * Check if a key exists in the FormData
	 */
	hasTypedKey<K extends keyof T>(key: K): boolean {
		return this.formData.has(key as string);
	}

	/**
	 * Delete a key from the FormData
	 */
	deleteTypedKey<K extends keyof T>(key: K): void {
		this.formData.delete(key as string);
	}

	/**
	 * Get all keys from the FormData
	 */
	getAllKeys(): string[] {
		// FormData iteration methods are not available in all environments
		// This is a simplified implementation that may not work in all cases
		const keys: string[] = [];
		try {
			// Try to use entries if available
			if ('entries' in this.formData && typeof this.formData.entries === 'function') {
				for (const [key] of this.formData.entries()) {
					keys.push(key);
				}
			}
		} catch {
			// Fallback: return empty array if entries is not available
		}
		return keys;
	}

	/**
	 * Get all entries from the FormData
	 */
	getAllEntries(): [string, FormDataEntryValue][] {
		const entries: [string, FormDataEntryValue][] = [];
		try {
			// Try to use entries if available
			if ('entries' in this.formData && typeof this.formData.entries === 'function') {
				for (const entry of this.formData.entries()) {
					entries.push(entry);
				}
			}
		} catch {
			// Fallback: return empty array if entries is not available
		}
		return entries;
	}
}

/**
 * Helper function to create a TypedFormData instance
 */
export function createTypedFormData<T extends Record<string, unknown>>(
	formData?: FormData,
): TypedFormData<T> {
	return new TypedFormData<T>(formData);
}

/**
 * Helper function to create a TypedFormData from an object
 */
export function createTypedFormDataFromObject<T extends Record<string, unknown>>(
	obj: T,
): TypedFormData<T> {
	const formData = new FormData();
	const typedFormData = new TypedFormData<T>(formData);

	for (const [key, value] of Object.entries(obj)) {
		if (value !== null && value !== undefined) {
			typedFormData.appendTypedValue(key as keyof T, value as T[keyof T]);
		}
	}

	return typedFormData;
}

/**
 * Helper function to safely get a file from FormData
 */
export function getTypedFile<K extends keyof T, T extends Record<string, unknown>>(
	formData: TypedFormData<T>,
	key: K,
): File | null {
	const value = formData.getTypedValue(key);
	return value instanceof File ? value : null;
}

/**
 * Helper function to safely get a string from FormData
 */
export function getTypedString<K extends keyof T, T extends Record<string, unknown>>(
	formData: TypedFormData<T>,
	key: K,
): string | null {
	const value = formData.getTypedValue(key);
	return typeof value === 'string' ? value : null;
}

/**
 * Helper function to safely get a number from FormData
 */
export function getTypedNumber<K extends keyof T, T extends Record<string, unknown>>(
	formData: TypedFormData<T>,
	key: K,
): number | null {
	const value = formData.getTypedValue(key);
	if (typeof value === 'number') return value;
	if (typeof value === 'string') {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : null;
	}
	return null;
}
